import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { apiClient } from '../../api';
import { errMessage, inputCls, inputStyle, primaryBtn, primaryBtnStyle } from '../../components/UI/ink';
import { Notice, SectionHead, panel } from '../../components/Settings/ui';

const ROLES = [
  ['hr', 'HR', 'Employee records, discipline and separation.'],
  ['legal', 'Legal counsel', 'Approves what an investigation may do.'],
  ['privacy', 'Privacy officer', 'Checks that monitoring is lawful (for example the DPO under PDPA).'],
];

const MODES = [
  ['approve', 'Approve first', 'The agent drafts the email. It is sent only after someone with playbook approval rights approves it in the run.'],
  ['auto', 'Send automatically', 'The agent sends the email as soon as it writes it. Every email is still kept in the run.'],
];

const blank = (role) => ({ role, name: '', email: '' });
const shape = (d) => JSON.stringify({ mode: d.mode, contacts: d.contacts.map(({ role, name, email }) => ({ role, name: name || '', email })) });

// The people a playbook agent may email directly (notify_contacts), instead of the shared alert channel.
export default function IncidentContactsSettings() {
  const { permissions = [] } = useAuth();
  const canEdit = permissions.includes('system:update');
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState({ email_configured: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  const apply = (data) => {
    const d = { mode: data.mode, contacts: (data.contacts || []).map(c => ({ role: c.role, name: c.name || '', email: c.email })) };
    setSaved(d);
    setForm(d);
    setMeta({ email_configured: data.email_configured });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/incident-contacts');
      apply(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Only administrators can change incident contacts.' : errMessage(err, 'Could not load incident contacts.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = saved && form && shape(saved) !== shape(form);
  const setContact = (i, patch) => setForm(f => ({ ...f, contacts: f.contacts.map((c, k) => (k === i ? { ...c, ...patch } : c)) }));
  const remove = (i) => setForm(f => ({ ...f, contacts: f.contacts.filter((_, k) => k !== i) }));
  const add = (role) => setForm(f => ({ ...f, contacts: [...f.contacts, blank(role)] }));

  const save = async () => {
    setBusy(true); setError(null); setOk(false);
    try {
      const contacts = form.contacts.filter(c => c.email.trim()).map(c => ({ role: c.role, name: c.name.trim(), email: c.email.trim() }));
      const { data } = await apiClient.put('/api/incident-contacts', { mode: form.mode, contacts });
      apply(data);
      setOk(true);
    } catch (err) {
      setError(errMessage(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <SectionHead title="Incident contacts" sub="People a playbook may email directly, such as the Insider Threat playbook. Agents choose a role; only the addresses listed here can receive mail." />
      {error && <Notice>{error}</Notice>}
      {form && !meta.email_configured && <Notice kind="warn">Email sending is not set up on this server yet, so emails will be recorded as failed until it is.</Notice>}

      {form && (
        <>
          <section className="flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border" style={panel}>
            <span className="text-sm font-semibold">How agent emails are sent</span>
            <div role="radiogroup" aria-label="Delivery mode" className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
              {MODES.map(([id, title, desc]) => {
                const on = form.mode === id;
                return (
                  <button key={id} type="button" role="radio" aria-checked={on} disabled={!canEdit} onClick={() => setForm(f => ({ ...f, mode: id }))}
                    className="flex flex-col gap-1 px-3.5 py-3 rounded-[10px] border text-left disabled:opacity-60"
                    style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-bg)', borderColor: on ? 'var(--accent)' : 'var(--surf-line-strong)' }}>
                    <span className="text-[13.5px] font-semibold">{title}</span>
                    <span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>{desc}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {ROLES.map(([role, title, desc]) => (
            <section key={role} className="flex flex-col gap-2.5 px-[18px] py-4 rounded-[14px] border" style={panel}>
              <span className="flex flex-col gap-[3px]"><span className="text-sm font-semibold">{title} <span className="font-mono text-[11px] font-normal" style={{ color: 'var(--ink-faint)' }}>{role}</span></span>
                <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>{desc}</span></span>
              {form.contacts.map((c, i) => c.role !== role ? null : (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <input aria-label={`${title} name`} placeholder="Name" value={c.name} disabled={!canEdit} onChange={e => setContact(i, { name: e.target.value })} className={`${inputCls} flex-[1_1_160px]`} style={{ ...inputStyle, width: 'auto' }} />
                  <input aria-label={`${title} email`} type="email" placeholder="name@company.com" value={c.email} disabled={!canEdit} onChange={e => setContact(i, { email: e.target.value })} className={`${inputCls} flex-[2_1_220px]`} style={{ ...inputStyle, width: 'auto' }} />
                  {canEdit && <button type="button" aria-label={`Remove ${title} contact`} onClick={() => remove(i)} className="w-11 h-11 flex items-center justify-center rounded-[10px]" style={{ color: 'var(--ink-muted)' }}><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {!form.contacts.some(c => c.role === role) && <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>No one yet. Agents cannot email this role.</span>}
              {canEdit && (
                <button type="button" onClick={() => add(role)} className="self-start inline-flex items-center gap-1.5 text-[13px] hover:underline" style={{ color: 'var(--ink-link)' }}>
                  <Plus className="w-3.5 h-3.5" />Add {title.toLowerCase()} contact
                </button>
              )}
            </section>
          ))}

          {canEdit && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={save} disabled={!dirty || busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Saving…' : 'Save contacts'}</button>
              {ok && !dirty && <span className="text-[13px]" style={{ color: 'var(--sev-good-fg)' }}>Saved.</span>}
              {dirty && <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>Unsaved changes</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
